import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Coins, MessageCircle, ChevronRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { usePageMeta, useFAQSchema } from '../../../hooks/useSEO';
import BreadcrumbSchema from '../../../components/seo/BreadcrumbSchema';

const TUITION_ROWS = [
  { course: 'Business / BBA',           low: 75000,  high: 95000 },
  { course: 'Engineering / Computer Sc.', low: 90000, high: 120000 },
  { course: 'Media & Design',            low: 80000, high: 110000 },
  { course: 'MBA (1-Year)',              low: 95000, high: 120000 },
  { course: 'MSc Data / AI / Fintech',   low: 85000, high: 120000 },
  { course: 'MSc Hospitality',           low: 85000, high: 110000 },
];

const LIVING_ROWS = [
  { area: 'Dubai International Academic City (DIAC)', total: 'AED 3,500 – 4,500', sample: 'AED 1,800 rent + AED 900 food + AED 350 metro · cheapest end' },
  { area: 'Al Barsha / Tecom',                         total: 'AED 4,000 – 5,500', sample: 'Sweet spot — close to Mall of the Emirates Metro' },
  { area: 'Dubai Marina / JLT',                         total: 'AED 5,500 – 7,500', sample: 'Premium · waterfront · DIFC commute friendly' },
];

const FAQS = [
  { q: 'What is the average annual tuition fee in Dubai for Indian students (AED)?', a: 'Undergraduate: AED 75,000 – 120,000 per year. Postgraduate: AED 85,000 – 120,000 per year. STEM and MBA programmes sit at the higher end. Fees are KHDA-regulated and published annually.' },
  { q: 'What is the cheapest area to live in Dubai as a student?', a: 'Dubai International Academic City (DIAC) — AED 3,500 to 4,500 per month all-in. It has the highest concentration of shared student housing and a direct Metro link to Dubai Mall and Marina.' },
  { q: 'Can I get a scholarship to study in Dubai?', a: 'Yes. KHDA-accredited campuses offer 10–50% merit scholarships based on Class 12 / Bachelor\u2019s GPA. Heriot-Watt, Middlesex, AUD and University of Birmingham Dubai run dedicated India scholarships ranging from AED 8,000 – 30,000 per year.' },
  { q: 'How much should I budget for a 2-year MBA in Dubai in INR?', a: 'Total programme cost (tuition + living): roughly INR 38 – 55 lakh for a top-tier 2-year MBA at AUD, Heriot-Watt or Middlesex. A 1-year MBA caps the budget around INR 22 – 30 lakh — Dubai\u2019s biggest cost advantage versus the UK or US.' },
];

const fmt = (n) => `AED ${n.toLocaleString('en-AE')}`;

export default function DubaiCostOfLivingPage() {
  usePageMeta(
    'Cost of Living & Tuition in Dubai 2026 — AED & INR Breakdown for Indian Students',
    'Complete 2026 cost guide for studying in Dubai: KHDA-published tuition in AED for UG and PG, area-wise monthly living cost tables (DIAC, Al Barsha, Marina), and INR conversion for Indian parents.'
  );
  useFAQSchema(FAQS);

  return (
    <div className="zaf-edu-platform bg-white text-slate-900" data-testid="dubai-cost-page">
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://zafco.in/' },
        { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
        { name: 'Study in Dubai', url: 'https://zafco.in/study-abroad/dubai/' },
        { name: 'Cost of Living & Tuition', url: 'https://zafco.in/study-abroad/dubai/cost-of-living-tuition/' },
      ]} />

      <section className="bg-gradient-to-br from-[#0F1B2C] to-[#162538] text-white py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <Link to="/study-abroad/dubai/" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-4 hover:text-amber-300">
            <ChevronRight className="w-3 h-3 rotate-180" /> Dubai Hub
          </Link>
          <h1 className="text-3xl md:text-5xl font-bold font-syne leading-tight">Cost of Living &amp; Tuition in Dubai — 2026</h1>
          <p className="mt-4 text-slate-300 max-w-3xl leading-relaxed">Every AED you&apos;ll spend — tuition by course, rent by area, food, transport — built from 2026 KHDA filings and verified by ZAFCO counsellors who place 500+ students into Dubai every cycle.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-2 flex items-center gap-2"><Coins className="w-6 h-6 text-[#D4AF37]" /> Tuition Fees (AED per year)</h2>
          <p className="text-slate-600 mb-6">KHDA-regulated 2026 fee bands. All Dubai universities publish these in AED; INR equivalents fluctuate with the rupee.</p>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[#0F1B2C] text-white">
                <tr><th className="text-left px-5 py-3 font-semibold">Course</th><th className="text-right px-5 py-3">Low (AED)</th><th className="text-right px-5 py-3">High (AED)</th><th className="text-right px-5 py-3">High (INR ≈)</th></tr>
              </thead>
              <tbody>
                {TUITION_ROWS.map((r) => (
                  <tr key={r.course} className="border-t border-slate-200 even:bg-white">
                    <td className="px-5 py-3 font-semibold text-[#0F1B2C]">{r.course}</td>
                    <td className="px-5 py-3 text-right font-mono text-slate-700">{fmt(r.low)}</td>
                    <td className="px-5 py-3 text-right font-mono font-semibold text-[#0F1B2C]">{fmt(r.high)}</td>
                    <td className="px-5 py-3 text-right font-mono text-slate-600">₹ {(r.high * 22.6 / 100000).toFixed(1)} L</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-2">INR conversion at 1 AED ≈ ₹22.6 (Feb 2026 reference). Engineering / Fintech / AI cluster at the higher band.</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-slate-50">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-2 flex items-center gap-2"><MapPin className="w-6 h-6 text-[#D4AF37]" /> Monthly Living Cost — by Area (AED)</h2>
          <p className="text-slate-600 mb-6">Pick your area based on lifestyle and commute. Most Indian students start in DIAC, then graduate to Al Barsha or Marina once they land a paid internship.</p>

          <div className="grid sm:grid-cols-3 gap-4">
            {LIVING_ROWS.map((row) => (
              <div key={row.area} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#D4AF37]/60 transition-colors">
                <h3 className="font-bold font-syne text-[#0F1B2C] mb-1">{row.area}</h3>
                <p className="text-2xl font-bold text-[#D4AF37] font-syne mb-2">{row.total}</p>
                <p className="text-xs text-slate-600 leading-relaxed">{row.sample}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-amber-50 border-l-4 border-[#D4AF37] rounded-r-xl flex gap-3">
            <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700"><strong>Tip:</strong> All KHDA-licensed unis allow part-time work up to 20 hours/week (AED 25–90/hour). Most students offset 30–50% of their living cost from the first semester.</p>
          </div>
        </div>
      </section>

      <FAQSection faqs={FAQS} />
      <FinalCTA source="dubai-cost-of-living-page" />
    </div>
  );
}

/* Tiny shared local components below (kept local to avoid premature abstraction) */

export function FAQSection({ faqs }) {
  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="container max-w-4xl mx-auto px-4 sm:px-6 md:px-12">
        <h2 className="text-2xl md:text-3xl font-bold font-syne mb-6">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {faqs.map((f) => (
            <details key={f.q} className="bg-slate-50 border border-slate-200 rounded-xl p-4 group">
              <summary className="font-bold font-syne text-slate-900 cursor-pointer flex items-center justify-between gap-3">
                {f.q}
                <ArrowRight className="w-4 h-4 text-[#D4AF37] group-open:rotate-90 transition-transform flex-shrink-0" />
              </summary>
              <p className="mt-3 text-slate-700 leading-relaxed text-sm">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCTA({ source }) {
  return (
    <section className="py-12 md:py-16 bg-gradient-to-br from-[#0F1B2C] to-[#0B1322] text-white">
      <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12 text-center">
        <h2 className="text-2xl md:text-3xl font-bold font-syne mb-4">Get a personalised Dubai 2026 budget plan</h2>
        <p className="text-slate-300 max-w-2xl mx-auto mb-6">15-min counselling call — our team will map your target course, area, scholarships and likely part-time income to land a realistic AED+INR plan.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="https://wa.me/919990533327?text=Hello%20Zafco,%20I%20want%20a%20Dubai%202026%20budget%20plan."
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-amber-400 text-[#0F1B2C] font-bold px-6 py-3 rounded-full"
            data-testid={`${source}-whatsapp`}
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp +91-9990533327
          </a>
          <Link
            to={`/study-abroad/book-counselling/?source=${source}`}
            className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 font-semibold px-6 py-3 rounded-full"
            data-testid={`${source}-counselling`}
          >
            <CheckCircle2 className="w-4 h-4" /> Book Free Counselling
          </Link>
        </div>
      </div>
    </section>
  );
}
